import React, { useCallback, useEffect, useRef, useState } from "react";

import { faBug } from "@fortawesome/pro-regular-svg-icons";
import { m } from "framer-motion";

import {
  AgentPlaygroundContent,
  PlaygroundState,
} from "src/agents/AgentVersionPage/AgentPlayground";
import { usePlaygroundContext } from "src/agents/AgentVersionPage/PlaygroundContext";
import { SideDrawer, useSideDrawer } from "src/agents/base/SideDrawer";
import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Icon } from "src/base-components/Icon";
import { usePowertools } from "src/powertools/hooks";
import { useWsInfo } from "src/powertools/useWsInfo";
import { getAgentLogsInsightsLink } from "src/powertools/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";

type AgentPlaygroundDrawerProps = {
  agentId: string;
  versionId: string;
  agentName: string;
};

export const AgentPlaygroundDrawer: React.FC<AgentPlaygroundDrawerProps> = ({
  agentId,
  versionId,
  agentName,
}) => {
  const [playgroundState, setPlaygroundState] = useState<PlaygroundState>({
    type: "input",
  });

  const { currentSessionId, sessionStatus, ping, resetSession } =
    usePlaygroundContext();

  const { workspace } = useWorkspaceContext();
  const wsInfo = useWsInfo(workspace);
  const isPowerToolsEnabled = usePowertools();

  // Handle close with explicit cleanup - reset for next open
  const handleClose = useCallback(
    (accept: () => void) => {
      setPlaygroundState({ type: "input" });
      resetSession();
      accept();
    },
    [resetSession],
  );

  const handleDebugLogs = useCallback(() => {
    if (wsInfo.data?.stack_name && wsInfo.data?.region && currentSessionId) {
      window.open(
        getAgentLogsInsightsLink(
          wsInfo.data.stack_name,
          wsInfo.data.region,
          currentSessionId,
        ),
        "_blank",
      );
    }
  }, [wsInfo.data?.stack_name, wsInfo.data?.region, currentSessionId]);

  // Ping when drawer opens (detect via isOpen from useSideDrawer)
  const { isOpen } = useSideDrawer("playground", {
    onCloseRequested: handleClose,
  });
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      ping(); // Uses current (potentially fresh) session ID
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, ping]);

  const handleReset = useCallback(() => {
    setPlaygroundState({ type: "input" });
    resetSession();
  }, [resetSession]);

  // Show extra actions when there's an active session (booting is just pre-warming, not a session)
  const hasActiveSession =
    currentSessionId !== null &&
    sessionStatus !== null &&
    sessionStatus !== "booting";
  const extraActions = hasActiveSession ? (
    <m.div
      animate={{ opacity: 1 }}
      className="flex items-center gap-2"
      initial={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: "easeIn" }}
    >
      {currentSessionId && (
        <CopyTextIcon
          dataLoc="copy-session-id-button"
          feedback="inline"
          size="xs"
          tooltip="Copy session ID"
          value={currentSessionId}
        />
      )}
      {isPowerToolsEnabled && wsInfo.data && (
        <Icon
          color="text-gray-500 hover:text-gray-700"
          dataLoc="test-debug-logs-button"
          icon={faBug}
          size="sm"
          onClick={handleDebugLogs}
        />
      )}
      <Button
        dataLoc="test-reset-button"
        size="sm"
        variant="secondary"
        onClick={handleReset}
      >
        Reset
      </Button>
    </m.div>
  ) : undefined;

  return (
    <SideDrawer
      dataLoc="agent-playground-drawer"
      drawerId="playground"
      header={{
        title: "Test Agent",
        description: "Try out your agent to see how it responds.",
        extraActions,
        closeDataLoc: "close-test-panel",
      }}
    >
      <AgentPlaygroundContent
        agentId={agentId}
        agentName={agentName}
        playgroundState={playgroundState}
        versionId={versionId}
        onPlaygroundStateChange={setPlaygroundState}
      />
    </SideDrawer>
  );
};
